import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Breadcrumb,
    Row,
    Col,
    Card,
    notification,
    Button,
    Spin,
    Image,
    Table,
    Space,
    Popconfirm,
    Tag,
    Modal,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SwapOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { DragHandle } from "../../components/DragHandle";
import http from "../../helpers/http";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/";
const base_url = `${API_URL}admin/signature-spaces`;
const image_url = `${API_URL}uploads/images/`;

function SignatureSpaceList() {
    const navigate = useNavigate();
    const [spaces, setSpaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reorderModalOpen, setReorderModalOpen] = useState(false);
    const [reorderList, setReorderList] = useState([]);
    const [reorderLoading, setReorderLoading] = useState(false);

    const fetchSpaces = async () => {
        setLoading(true);
        try {
            const res = await http.get(`${base_url}?limit=1000`);
            const data = res?.data || res;
            if (data?.spaces) {
                setSpaces(data.spaces);
            } else if (Array.isArray(data)) {
                setSpaces(data);
            } else {
                setSpaces([]);
            }
        } catch (error) {
            notification.error({
                message: "Error fetching signature spaces",
                description: error?.message,
            });
            setSpaces([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await http.delete(`${base_url}/${id}`);
            notification.success({ message: "Signature Space deleted successfully" });
            fetchSpaces();
        } catch (error) {
            notification.error({
                message: "Error deleting signature space",
                description: error?.message,
            });
        }
    };

    const handleOpenReorder = () => {
        setReorderList([...spaces]);
        setReorderModalOpen(true);
    };

    const handleReorderSubmit = async () => {
        setReorderLoading(true);
        try {
            const orders = reorderList.map((item, index) => ({
                id: item._id,
                order: index + 1
            }));

            await http.post(`${base_url}/reorder`, { orders });
            notification.success({ message: "Order updated successfully" });
            setReorderModalOpen(false);
            fetchSpaces();
        } catch (error) {
            notification.error({ message: "Error updating order", description: error.message });
        } finally {
            setReorderLoading(false);
        }
    };

    const columns = [
        {
            title: "Image",
            dataIndex: "img",
            key: "img",
            width: 80,
            render: (img) =>
                img ? (
                    <Image
                        width={50}
                        height={50}
                        src={image_url + img}
                        style={{ objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    <div
                        style={{
                            width: 50,
                            height: 50,
                            background: "#f0f0f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            borderRadius: 4,
                        }}
                    >
                        No Image
                    </div>
                ),
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            ellipsis: true,
            sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
        },
        {
            title: "Order ID",
            dataIndex: "orderId",
            key: "orderId",
            width: 100,
            sorter: (a, b) => (a.orderId || 0) - (b.orderId || 0),
        },
        {
            title: "Product Link",
            dataIndex: "productLink",
            key: "productLink",
            ellipsis: true,
            render: (link) => link || <i style={{ color: '#ccc' }}>None</i>
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={status === "active" ? "green" : "red"}>
                    {status?.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            fixed: "right",
            width: 160,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => navigate(`/signature-spaces/edit/${record._id}`)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this signature space?"
                        onConfirm={() => handleDelete(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        fetchSpaces();
    }, []);

    return (
        <div>
            <Breadcrumb style={{ margin: "16px 0" }}>
                <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
                <Breadcrumb.Item>Signature Spaces</Breadcrumb.Item>
            </Breadcrumb>

            <Row>
                <Col span={24}>
                    <Card
                        title="Signature Spaces"
                        extra={
                            <Space>
                                <Button
                                    icon={<SwapOutlined rotate={90} />}
                                    onClick={handleOpenReorder}
                                    disabled={spaces.length < 2}
                                >
                                    Change Order
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => navigate("/signature-spaces/create")}
                                >
                                    Add New Signature Space
                                </Button>
                            </Space>
                        }
                    >
                        {loading ? (
                            <div style={{ textAlign: "center", padding: 50 }}>
                                <Spin size="large" />
                            </div>
                        ) : (
                            <Table
                                columns={columns}
                                dataSource={spaces}
                                rowKey="_id"
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: 800 }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>

            <Modal
                title={`Reorder Signature Spaces`}
                open={reorderModalOpen}
                onOk={handleReorderSubmit}
                onCancel={() => setReorderModalOpen(false)}
                confirmLoading={reorderLoading}
                okText="Save Order"
                width={600}
                destroyOnClose
            >
                <div style={{ marginTop: 10 }}>
                    <p style={{ color: "#888", marginBottom: 15 }}>
                        Drag and drop items to change their display order on the website.
                    </p>
                    <DragDropContext
                        onDragEnd={(result) => {
                            if (!result.destination) return;
                            const items = Array.from(reorderList);
                            const [reorderedItem] = items.splice(result.source.index, 1);
                            items.splice(result.destination.index, 0, reorderedItem);
                            setReorderList(items);
                        }}
                    >
                        <Droppable droppableId="reorder-list">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {reorderList.map((item, index) => (
                                        <Draggable key={item._id} draggableId={item._id} index={index}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        background: snapshot.isDragging ? "#fafafa" : "#fff",
                                                        padding: "10px",
                                                        border: "1px solid #f0f0f0",
                                                        borderRadius: "4px",
                                                        marginBottom: "8px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        boxShadow: snapshot.isDragging ? "0 2px 8px rgba(0,0,0,0.15)" : "none"
                                                    }}
                                                >
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <Image
                                                            width={40} height={40}
                                                            src={image_url + item.img}
                                                            style={{ objectFit: "cover", borderRadius: 4, marginRight: 15 }}
                                                            preview={false}
                                                            fallback="/placeholder.jpg"
                                                        />
                                                        <span>{item.title}</span>
                                                    </div>
                                                    <DragHandle {...provided.dragHandleProps} />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </Modal>
        </div>
    );
}

export default SignatureSpaceList;
